import TextField from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useState, useEffect } from "react";
import Name, { InvalidNameError } from "_shared/names/name";

const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
    width: '100%'
  },
  marginBottom: theme.spacing(1.5),
  minWidth: theme.spacing(40),
  ":first-of-type": {
    marginRight: theme.spacing(1.5)
  }
}));

type NameInputProps = {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  name?: Name;
  onChange?: (name?: Name, error?: string) => void;
};

const NameInput = forwardRef(
  function NameInput(props: NameInputProps, _ref) {
  const { 
    idPrefix, 
    className, 
    variant, 
    name, 
    onChange 
  } = props;
  
  const [firstNameFieldText, setFirstNameFieldText] = useState<string>(name?.firstName ?? '');
  const [lastNameFieldText, setLastNameFieldText] = useState<string>(name?.lastName ?? '');
  const [nameFieldError, setNameFieldError] = useState<string>('');

  useEffect(() => {
    setFirstNameFieldText(name?.firstName ?? '');
    setLastNameFieldText(name?.lastName ?? '');
  }, [name]);



  function handleNameChanged(firstName?: string, lastName?: string) {
    let newName: Name | undefined;
    let newErrors: string = '';

    try {
      newName = buildName(firstName, lastName);
      setNameFieldError('');
    } catch (error) {
      if (!(error instanceof InvalidNameError)) return;
      newErrors = error.message;
      setNameFieldError(newErrors);
    } finally {
      onChange?.(newName, newErrors);
    }
  }

  return (
    <div>
      <InputField
        id={idPrefix ? idPrefix + "-first-name-input" : "first-name-input"}
        className={className}
        label="First Name"
        onChange={(event) => {
          setFirstNameFieldText(event.target.value);
          handleNameChanged(event.target.value, lastNameFieldText);
        }}
        required
        value={firstNameFieldText}
        error={!!nameFieldError}
        helperText={nameFieldError}
        variant={variant}
        autoComplete="given-name"
      />
      <InputField
        id={idPrefix ? idPrefix + "-last-name-input" : "last-name-input"}
        className={className}
        label="Last Name"
        onChange={(event) => {
          setLastNameFieldText(event.target.value);
          handleNameChanged(firstNameFieldText, event.target.value);
        }}
        required
        value={lastNameFieldText}
        error={!!nameFieldError}
        helperText={nameFieldError}
        variant={variant}
        autoComplete="family-name"
      />
    </div>
  );
});

function buildName(firstName?: string, lastName?: string): Name | undefined {
  try {
    if (!firstName && !lastName) {
      throw new InvalidNameError(
        "First name and last name are required",
        { firstName: firstName ?? '', lastName: lastName ?? '' }
      );
    }
    return Name.create({ params: { firstName, lastName }, options: {silenceSchemaErrors: true} }) ?? undefined;
  } catch (error) {
    if (error instanceof InvalidNameError) {
      throw error;
    }
  }
}
export default NameInput;
